import Title from "@/components/common/Title";
import { Card, CardContent } from "@patient-watch/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { services as s } from "@/services";
import { useNav } from "@/utils/nav";
import { createFileRoute } from "@tanstack/react-router";
import { organisationByIdQueryOptions } from "@/features/auth/api/selectOrganisationById";
import { selectUsersByOrganisationIdQueryOptions } from "@/features/organisations/api/selectUsersByOrganisationId";

const CLINICAL_ROLES = new Set(["clinician", "secretary"]);

function isClinicianOrSecretary(user: { roles: string[] }): boolean {
    return user.roles.some((r) => CLINICAL_ROLES.has(String(r).toLowerCase()));
}

// Main component
const OrganisationPage = () => {
    const { organisationId } = Route.useParams();
    const { organisation, allUsers } = Route.useLoaderData();

    const careTeamRows = allUsers.filter(isClinicianOrSecretary).map(({ name, email, user_id, roles }) => ({
        name,
        email,
        user_id,
        roles: roles.filter((r) => CLINICAL_ROLES.has(String(r).toLowerCase())),
    }));

    useNav([
        { label: "Profile", route: { to: "/profile" } },
        {
            label: "Organisation",
            route: {
                to: "/organisation/$organisationId",
                params: { organisationId },
            },
        },
    ]);

    if (!organisation) {
        return <div>Organisation not found</div>;
    }

    const orgName = organisation.organisation_name;

    return (
        <section className="lg-width">
            <Title words={orgName} />

            {careTeamRows.length > 0 ? (
                <Card className="mt-6">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Roles</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {careTeamRows.map(({ name, email, user_id, roles }) => (
                                    <TableRow key={user_id}>
                                        <TableCell className="font-medium">{name}</TableCell>
                                        <TableCell className="text-slate-600">{email}</TableCell>
                                        <TableCell className="text-slate-500 text-xs uppercase tracking-wider">
                                            {roles.length ? s.capitaliseSentence(roles.join(", ")) : " - "}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            ) : (
                <Card className="mt-6">
                    <CardContent className="pt-6">
                        <p className="text-slate-600">No secretaries or clinicians found in this organisation.</p>
                    </CardContent>
                </Card>
            )}
        </section>
    );
};

export const Route = createFileRoute("/_app/organisation/$organisationId/")({
    loader: async ({ context, params }) => {
        const { queryClient } = context;
        const organisationId = params.organisationId;

        const organisation = await queryClient.ensureQueryData(organisationByIdQueryOptions(organisationId));

        // Fetch all users for this organisation directly from users_organisations_roles
        const allUsers = await queryClient.ensureQueryData(selectUsersByOrganisationIdQueryOptions(organisationId));

        return { organisation, allUsers };
    },
    component: OrganisationPage,
});
